import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onApply: (startDate: string, endDate: string) => void;
  label?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onApply,
  label = 'Change date range'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStart, setTempStart] = useState(startDate);
  const [tempEnd, setTempEnd] = useState(endDate);
  
  // Each calendar navigates independently so the "from" and "to" months
  // can be paged to completely different points in time.
  const [leftView, setLeftView] = useState(() => {
    const d = startDate ? new Date(startDate) : new Date();
    return { month: d.getMonth(), year: d.getFullYear() };
  });

  const [rightView, setRightView] = useState(() => {
    const d = endDate ? new Date(endDate) : new Date();
    // If same month as start, default to showing the next month
    const startD = startDate ? new Date(startDate) : new Date();
    if (d.getMonth() === startD.getMonth() && d.getFullYear() === startD.getFullYear()) {
      const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      return { month: next.getMonth(), year: next.getFullYear() };
    }
    return { month: d.getMonth(), year: d.getFullYear() };
  });

  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  const handleOpen = () => {
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dropdownWidth = 550;
      const left = Math.min(
        Math.max(10, rect.left + window.scrollX),
        window.innerWidth + window.scrollX - dropdownWidth - 10
      );
      setDropdownPos({
        top: rect.bottom + window.scrollY + 8,
        left
      });
      setTempStart(startDate);
      setTempEnd(endDate);
    }
    setIsOpen(!isOpen);
  };

  const getDaysInMonth = (month: number, year: number) => {
    const date = new Date(year, month, 1);
    const days = [];
    const firstDayIndex = date.getDay();
    const lastDay = new Date(year, month + 1, 0).getDate();

    // Padding for start of month
    for (let i = 0; i < firstDayIndex; i++) {
      days.push(null);
    }

    for (let i = 1; i <= lastDay; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  // Format using local date parts (not toISOString, which converts to UTC
  // and rolls midnight back a day in timezones ahead of UTC).
  const toLocalDateStr = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const isSelected = (date: Date) => {
    if (!date) return false;
    const dateStr = toLocalDateStr(date);
    return dateStr === tempStart || dateStr === tempEnd;
  };

  const isInRange = (date: Date) => {
    if (!date || !tempStart || !tempEnd) return false;
    const dateStr = toLocalDateStr(date);
    return dateStr > tempStart && dateStr < tempEnd;
  };

  const handleDateClick = (date: Date) => {
    if (!date) return;
    const dateStr = toLocalDateStr(date);

    if (!tempStart || (tempStart && tempEnd)) {
      setTempStart(dateStr);
      setTempEnd('');
    } else {
      if (dateStr < tempStart) {
        setTempEnd(tempStart);
        setTempStart(dateStr);
      } else {
        setTempEnd(dateStr);
      }
    }
  };

  const changeMonth = (calendar: 'left' | 'right', direction: number) => {
    const setView = calendar === 'left' ? setLeftView : setRightView;
    setView(prev => {
      const newDate = new Date(prev.year, prev.month + direction, 1);
      return { month: newDate.getMonth(), year: newDate.getFullYear() };
    });
  };

  const renderCalendar = (view: { month: number, year: number }, calendar: 'left' | 'right') => {
    const days = getDaysInMonth(view.month, view.year);
    return (
      <div style={{ width: '250px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', padding: '0 5px' }}>
          <span style={{ fontWeight: 600, fontSize: '14px' }}>{months[view.month]} {view.year}</span>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => changeMonth(calendar, -1)} style={navBtnStyle}><FontAwesomeIcon icon={faChevronLeft} /></button>
            <button onClick={() => changeMonth(calendar, 1)} style={navBtnStyle}><FontAwesomeIcon icon={faChevronRight} /></button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
          {daysOfWeek.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: '12px', color: '#666', paddingBottom: '8px' }}>{d}</div>
          ))}
          {days.map((d, i) => {
            const selected = d && isSelected(d);
            const inRange = d && isInRange(d);
            return (
              <div 
                key={i} 
                onClick={() => d && handleDateClick(d)}
                style={{
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: d ? 'pointer' : 'default',
                  fontSize: '13px',
                  borderRadius: selected ? '50%' : '0',
                  backgroundColor: selected ? '#000' : inRange ? '#f0f0f0' : 'transparent',
                  color: selected ? '#fff' : d ? '#333' : 'transparent',
                  fontWeight: selected ? 'bold' : 'normal',
                  position: 'relative',
                  transition: 'background-color 0.2s'
                }}
              >
                {d ? d.getDate() : ''}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const dropdown = isOpen ? (
    <div
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: dropdownPos.top,
        left: dropdownPos.left,
        backgroundColor: '#fff',
        border: '1px solid #ddd',
        borderRadius: '12px',
        padding: '24px',
        zIndex: 99999,
        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        minWidth: '550px'
      }}
    >
      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '5px' }}>{label}</div>
      
      <div style={{ display: 'flex', gap: '30px', borderBottom: '1px solid #eee', paddingBottom: '20px' }}>
        {renderCalendar(leftView, 'left')}
        {renderCalendar(rightView, 'right')}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '5px' }}>
        <button
          onClick={() => setIsOpen(false)}
          style={{
            padding: '8px 24px',
            borderRadius: '20px',
            border: '1px solid #ddd',
            background: '#fff',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          Cancel
        </button>
        <button
          disabled={!tempStart || !tempEnd}
          onClick={() => {
            onApply(tempStart, tempEnd);
            setIsOpen(false);
          }}
          style={{
            padding: '8px 24px',
            borderRadius: '20px',
            border: 'none',
            background: (!tempStart || !tempEnd) ? '#ccc' : '#000',
            color: '#fff',
            cursor: (!tempStart || !tempEnd) ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          Apply
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        onClick={handleOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #ced4da',
          borderRadius: '4px',
          background: '#fff',
          cursor: 'pointer',
          fontSize: '14px',
          color: '#333',
          fontFamily: 'inherit',
          boxSizing: 'border-box'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#666' }} />
          <span>{startDate && endDate ? `${startDate} - ${endDate}` : 'Select date range'}</span>
        </span>
        <FontAwesomeIcon icon={isOpen ? faChevronLeft : faChevronRight} style={{ fontSize: '10px', transform: 'rotate(90deg)' }} />
      </button>
      {isOpen && ReactDOM.createPortal(dropdown, document.body)}
    </div>
  );
};

const navBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '5px',
  color: '#666',
  fontSize: '12px'
};

export default DateRangePicker;
